import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import VideoCall from './VideoCall';
import { VIDEO_CALL_EXPAND_EVENT } from '@/constants/videoCall';

const DEFAULT_MINI_SIZE = { width: 400, height: 340 };
const MIN_MINI_WIDTH = 240;
const MIN_MINI_HEIGHT = 180;
const VIEWPORT_GUTTER = 20;
const RESIZE_DIRECTIONS = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const clampSize = ({ width, height }) => ({
  width: clamp(width, MIN_MINI_WIDTH, window.innerWidth - VIEWPORT_GUTTER * 2),
  height: clamp(height, MIN_MINI_HEIGHT, window.innerHeight - VIEWPORT_GUTTER * 2),
});

const clampPosition = ({ left, top }, { width, height }) => ({
  left: clamp(left, VIEWPORT_GUTTER, window.innerWidth - width - VIEWPORT_GUTTER),
  top: clamp(top, VIEWPORT_GUTTER, window.innerHeight - height - VIEWPORT_GUTTER),
});

const getMiniPosition = (size) => ({
  left: Math.max(VIEWPORT_GUTTER, window.innerWidth - size.width - VIEWPORT_GUTTER),
  top: Math.max(VIEWPORT_GUTTER, window.innerHeight - size.height - VIEWPORT_GUTTER),
});

// Area right of the sidebar and below the page header, up to the viewport edges.
const HEADER_SELECTOR = '.page-header-container, .hub-header-container';

const getHeaderBottom = () =>
  Array.from(document.querySelectorAll(HEADER_SELECTOR)).reduce((bottom, element) => {
    const rect = element.getBoundingClientRect();
    const isVisibleTopBar = rect.height > 0 && rect.top < window.innerHeight / 2;
    return isVisibleTopBar ? Math.max(bottom, rect.bottom) : bottom;
  }, 0);

const measureContentArea = () => {
  const sider = document.querySelector('.mainpage-sider');
  const left = (sider ? Math.max(0, sider.getBoundingClientRect().right) : 0) + VIEWPORT_GUTTER;
  const top = Math.max(0, getHeaderBottom()) + VIEWPORT_GUTTER;
  return {
    position: { left, top },
    size: {
      width: Math.max(MIN_MINI_WIDTH, window.innerWidth - left - VIEWPORT_GUTTER),
      height: Math.max(MIN_MINI_HEIGHT, window.innerHeight - top - VIEWPORT_GUTTER),
    },
  };
};

const VideoCallModal = () => {
  const visible = useSelector((state) => state.videoCall.videoCallModalVisible);
  const meetingId = useSelector((state) => state.videoCall.meetingInfo?.meeting_id);
  const [autoCloseTimer, setAutoCloseTimer] = useState(null);
  const [miniMeetingId, setMiniMeetingId] = useState(null);
  const [miniPosition, setMiniPosition] = useState(null);
  const [miniSize, setMiniSize] = useState(DEFAULT_MINI_SIZE);
  const dragStart = useRef(null);
  const resizeStart = useRef(null);
  const measuredMeetingId = useRef(null);

  const applyContentArea = () => {
    const { size, position } = measureContentArea();
    const nextSize = clampSize(size);
    setMiniSize(nextSize);
    setMiniPosition(clampPosition(position, nextSize));
  };

  useEffect(() => {
    if (!visible && autoCloseTimer) clearTimeout(autoCloseTimer);
  }, [visible, autoCloseTimer]);

  useEffect(() => {
    const expand = () => setMiniMeetingId(null);
    window.addEventListener(VIDEO_CALL_EXPAND_EVENT, expand);
    return () => window.removeEventListener(VIDEO_CALL_EXPAND_EVENT, expand);
  }, []);

  useEffect(() => {
    const isMini = miniMeetingId === meetingId && !!meetingId;
    if (!isMini) return undefined;

    const keepInsideViewport = () => {
      const nextSize = clampSize(miniSize);
      setMiniSize(nextSize);
      setMiniPosition((position) => clampPosition(position ?? getMiniPosition(nextSize), nextSize));
    };

    window.addEventListener('resize', keepInsideViewport);
    return () => window.removeEventListener('resize', keepInsideViewport);
  }, [miniMeetingId, meetingId, miniSize]);

  const minimize = () => {
    if (measuredMeetingId.current !== meetingId) {
      measuredMeetingId.current = meetingId;
      applyContentArea();
    }
    setMiniMeetingId(meetingId);
  };

  const handleDragStart = (event) => {
    if (event.target.closest('button')) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStart.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      position: miniPosition || getMiniPosition(miniSize),
    };
  };

  const handleDragMove = (event) => {
    if (dragStart.current?.pointerId !== event.pointerId) return;
    const { x, y, position } = dragStart.current;
    setMiniPosition(
      clampPosition(
        { left: position.left + event.clientX - x, top: position.top + event.clientY - y },
        miniSize
      )
    );
  };

  const handleResizeStart = (direction) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    resizeStart.current = {
      pointerId: event.pointerId,
      direction,
      x: event.clientX,
      y: event.clientY,
      size: miniSize,
      position: miniPosition || getMiniPosition(miniSize),
    };
  };

  const handleResizeMove = (event) => {
    if (resizeStart.current?.pointerId !== event.pointerId) return;
    const { direction, x, y, size, position } = resizeStart.current;
    const dx = event.clientX - x;
    const dy = event.clientY - y;
    let { width, height } = size;
    let { left, top } = position;

    if (direction.includes('e')) {
      width = clamp(size.width + dx, MIN_MINI_WIDTH, window.innerWidth - VIEWPORT_GUTTER - left);
    }
    if (direction.includes('w')) {
      const right = position.left + size.width;
      width = clamp(size.width - dx, MIN_MINI_WIDTH, right - VIEWPORT_GUTTER);
      left = right - width;
    }
    if (direction.includes('s')) {
      height = clamp(size.height + dy, MIN_MINI_HEIGHT, window.innerHeight - VIEWPORT_GUTTER - top);
    }
    if (direction.includes('n')) {
      const bottom = position.top + size.height;
      height = clamp(size.height - dy, MIN_MINI_HEIGHT, bottom - VIEWPORT_GUTTER);
      top = bottom - height;
    }

    setMiniSize({ width, height });
    setMiniPosition({ left, top });
  };

  const handlePointerMove = (event) => {
    handleDragMove(event);
    handleResizeMove(event);
  };

  const handleDragEnd = () => {
    dragStart.current = null;
    resizeStart.current = null;
  };

  if (!visible) return null;

  const isMiniWindow = miniMeetingId === meetingId && !!meetingId;
  const renderedSize = clampSize(miniSize);
  const renderedPosition = miniPosition ?? getMiniPosition(renderedSize);

  return (
    <div
      className={`video-call-host ${isMiniWindow ? 'video-call-host-mini' : 'video-call-host-expanded'}`}
      onPointerDown={isMiniWindow ? handleDragStart : undefined}
      onPointerMove={isMiniWindow ? handlePointerMove : undefined}
      onPointerUp={isMiniWindow ? handleDragEnd : undefined}
      onPointerCancel={isMiniWindow ? handleDragEnd : undefined}
      onLostPointerCapture={isMiniWindow ? handleDragEnd : undefined}
      style={
        isMiniWindow
          ? {
              left: renderedPosition.left,
              top: renderedPosition.top,
              width: renderedSize.width,
              height: renderedSize.height,
            }
          : undefined
      }
    >
      {isMiniWindow &&
        RESIZE_DIRECTIONS.map((direction) => (
          <div
            key={direction}
            aria-hidden="true"
            className={`video-call-resize-handle video-call-resize-handle-${direction}`}
            onPointerDown={handleResizeStart(direction)}
          />
        ))}
      <VideoCall
        autoCloseTimer={autoCloseTimer}
        setAutoCloseTimer={setAutoCloseTimer}
        isMini={isMiniWindow}
        onMinimize={minimize}
        onExpand={() => setMiniMeetingId(null)}
        onMiniDragStart={handleDragStart}
      />
    </div>
  );
};

export default VideoCallModal;
